import React from 'react';
import PropTypes from 'prop-types';
import {formatMoney} from '@shopify/theme-currency';

import StarsEmpty from './StarsEmpty';
import StarsFull from './StarsFull';

const MONEY_FORMAT = window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : '${{amount}}';

const CUSTOMER_TYPE = window.theme && window.theme.customerType ? window.theme.customerType : null;

const CUSTOMER_DISCOUNTS = {
  consultant: 'consultant_discount:',
  team_lead: 'team_leader_discount:',
  staff: 'staff_discount:',
};

const SearchHit = ({hit}) => {
  const {
    handle,
    image,
    title,
    variant_title,
    price,
    compare_at_price,
    meta,
    inventory_management,
    inventory_available,
    tags,
  } = hit;

  /**
   * Check if this customer is entitled to a discount based their customer type and the products tags
   * Discounts apply to consultants, team leads and staff
   */
  let discountPercentage = 0;
  if (CUSTOMER_TYPE in CUSTOMER_DISCOUNTS) {
    const discountTag = tags.find(tag => tag.includes(CUSTOMER_DISCOUNTS[CUSTOMER_TYPE]));
    if (discountTag) {
      discountPercentage = Number(discountTag.split(':')[1]);
    }
  }


  const finalPrice = discountPercentage > 0 ? ((100 - discountPercentage) * price) / 100 : price;
  const finalCompareAtPrice = discountPercentage > 0 ? price : compare_at_price;

  const Badge = ({label, variation = '', style = {}}) => (
    <span className={`hit-badge ${variation}`} style={style}>
      {label}
    </span>
  );
  Badge.propTypes = {
    label: PropTypes.string,
    variation: PropTypes.string,
    style: PropTypes.object,
  };

  const itemOnSale = finalCompareAtPrice > finalPrice;
  const SalePrices = ({price, compare_at_price}) => (
    <>
      <span className="hit-price__sale">{formatMoney(price * 100, MONEY_FORMAT)}</span>
      <s className="hit-price__original">{formatMoney(compare_at_price * 100, MONEY_FORMAT)}</s>
    </>
  );

  SalePrices.propTypes = {
    price: PropTypes.number,
    compare_at_price: PropTypes.number,
  };

  /**
   * Reviews come from Okendo in the form of metafields.
   * These metafields have been set inside the algolia app to be passed to the index in the meta key.
   */
  const hasReviewMetrics = meta && meta.okendo && meta.okendo.ReviewAverageValue && meta.okendo.ReviewCount;
  const reviewAverageValue = hasReviewMetrics ? meta.okendo.ReviewAverageValue : 0;
  const reviewCount = hasReviewMetrics ? meta.okendo.ReviewCount : 0;
  const Reviews = ({count, average}) => {
    const averageFloat = parseFloat(average);
    const percent = (averageFloat / 5) * 100;
    return (
      <div className="hit-reviews">
        <div className="hit-reviews__a11y visually-hidden">Rated {average} out of 5.</div>
        <div className="stars-container stars-container__empty">
          <div className="fixed">
            <div className="percent">
              <StarsEmpty />
            </div>
          </div>
        </div>
        <div className="stars-container stars-container__full">
          <div className="fixed">
            <div className="percent" style={{width: `${percent}%`}}>
              <StarsFull />
            </div>
          </div>
          <span className="review-count">| {count}</span>
        </div>
      </div>
    );
  };

  /**
   * Check if this product is a bundle and if so we don't want to show the out of stock badge
   */
  const isBundle = tags.some(tag => tag.includes('bundle_item:'));

  /**
   * Check if this product is a preregister and if so show a preregister badge
   */
  const isPreregister =
    tags.some(tag => tag === 'preregister') && inventory_management === 'shopify' && !inventory_available;

  /**
   * Check if this product is a preorder and if so show a preorder badge in a variant-level
   */
  const variantPreorder = tags.filter(tag => tag.includes("pre-orderVariant")); 
  const isPreorder = variantPreorder.some(tag => tag.includes(variant_title));
  
  /**
   * Check if this product has a custom badge
   */
  const customBadgeTag = tags.find(tag => tag.includes('badge:'));
  let customBadgeLabel = null;
  const customBadgeStyles = {};
  if (customBadgeTag) {
    const customBadgeParts = customBadgeTag.split(':');
    customBadgeLabel = customBadgeParts.length > 1 ? customBadgeParts[1] : null;
    const customBadgeColour = customBadgeParts.length > 2 ? customBadgeParts[2] : null;
    const customBadgeBackgroundColour = customBadgeParts.length > 3 ? customBadgeParts[3] : null;
    if (customBadgeColour) {
      customBadgeStyles['--badge-colour'] = customBadgeColour;
      customBadgeStyles['--badge-border-colour'] = customBadgeColour;
    }
    if (customBadgeBackgroundColour) {
      customBadgeStyles['--badge-background-colour'] = customBadgeBackgroundColour;
      customBadgeStyles['--badge-border-colour'] = customBadgeBackgroundColour;
    }
  }
  const hasCustomBadge = customBadgeLabel !== null;

  /**
   * Display the appropriate badge based on sold out/preorder status
   */
  let badge = null;
  if (hasCustomBadge) {
    badge = <Badge label={customBadgeLabel} variation="hit-badge--custom" style={customBadgeStyles} />;
  } else if (isPreorder && inventory_available) {
    badge = <Badge label={window.theme.strings.preorder} variation="hit-badge--preorder" />;
  } else if (isPreregister) {
    badge = <Badge label={window.theme.strings.preregister} variation="hit-badge--preregister" />;
  } else if (inventory_management === 'shopify' && !inventory_available && !isBundle) {
    badge = <Badge label={window.theme.strings.soldOut} />;
  }

  Reviews.propTypes = {
    count: PropTypes.number,
    average: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  const product_url = `/products/${handle}`;

  return (
    <a href={product_url} className="hit">
      <div className="hit-image">
        <img src={image} alt={title} />
        {badge}
      </div>
      <div className="hit-details">
        <div className="hit-reviews-price">
          <Reviews count={reviewCount} average={reviewAverageValue} />
        </div>
        <div className="hit-title">
          {title}
          {variant_title !== 'Default Title' && ` - ${variant_title}`}
          <div className="hit-price">
            {itemOnSale ? (
              <SalePrices price={finalPrice} compare_at_price={finalCompareAtPrice} />
            ) : (
              <span>{formatMoney(finalPrice * 100, MONEY_FORMAT)}</span>
            )}
          </div>
        </div>
      </div>
    </a>
  );
};

SearchHit.propTypes = {
  hit: PropTypes.shape({
    handle: PropTypes.string,
    image: PropTypes.string,
    title: PropTypes.string,
    variant_title: PropTypes.string,
    price: PropTypes.number,
    compare_at_price: PropTypes.number,
    meta: PropTypes.object,
    inventory_management: PropTypes.string,
    inventory_available: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    tags: PropTypes.array,
  }),
};

export default SearchHit;
